import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  ArrowUpDown, ArrowUp, ArrowDown, Pencil, Trash2, Settings2,
  Download, Copy, RefreshCw, ChevronsUpDown, Check, X, FilterX,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { escapeCSVValue } from '../utils/csvExport';
import type { Stream, StreamsFilters } from '../types';

// =============================================================================
// Column Definition
// =============================================================================

interface FilterOption {
  value: string;
  label: string;
}

interface StreamColumnDef {
  key: string;
  labelKey: string;
  defaultVisible?: boolean;
  sortKey?: StreamsFilters['sortKey'];
  filterType?: 'text' | 'date-range' | 'searchable-select';
  getValue: (stream: Stream) => string | null;
  noFilter?: boolean;
  headerClassName?: string;
  cellClassName?: string;
}

// =============================================================================
// Column definitions
// =============================================================================

const COLUMNS: StreamColumnDef[] = [
  {
    key: 'name',
    labelKey: 'table.name',
    defaultVisible: true,
    sortKey: 'name',
    filterType: 'text',
    getValue: (s) => s.name,
    cellClassName: 'font-medium',
  },
  {
    key: 'sensorName',
    labelKey: 'table.sensorName',
    defaultVisible: true,
    sortKey: 'sensorName',
    filterType: 'text',
    getValue: (s) => s.sensorName,
  },
  {
    key: 'organisationName',
    labelKey: 'table.organisationName',
    defaultVisible: true,
    sortKey: 'organisationName',
    filterType: 'searchable-select',
    getValue: (s) => s.organisationName,
    cellClassName: 'max-w-40 truncate',
  },
  {
    key: 'probeSerial',
    labelKey: 'table.probeSerial',
    defaultVisible: true,
    sortKey: 'probeSerial',
    filterType: 'text',
    getValue: (s) => s.probeSerial,
  },
  {
    key: 'transmitterName',
    labelKey: 'table.transmitterName',
    defaultVisible: false,
    sortKey: 'transmitterName',
    filterType: 'text',
    getValue: (s) => s.transmitterName,
  },
  {
    key: 'calibrationCertNumber',
    labelKey: 'table.calibrationCertNumber',
    defaultVisible: true,
    sortKey: 'calibrationCertNumber',
    filterType: 'text',
    getValue: (s) => s.calibrationCertNumber,
  },
  {
    key: 'calibrationDue',
    labelKey: 'table.calibrationDue',
    defaultVisible: true,
    sortKey: 'calibrationDue',
    filterType: 'date-range',
    getValue: (s) => s.calibrationDue,
    headerClassName: 'w-28',
  },
  {
    key: 'updatedAt',
    labelKey: 'table.updatedAt',
    defaultVisible: true,
    sortKey: 'updatedAt',
    filterType: 'date-range',
    getValue: (s) => s.updatedAt,
    headerClassName: 'w-36',
  },
  {
    key: 'createdAt',
    labelKey: 'table.createdAt',
    defaultVisible: false,
    sortKey: 'createdAt',
    filterType: 'date-range',
    getValue: (s) => s.createdAt,
    headerClassName: 'w-36',
  },
  {
    key: 'actions',
    labelKey: '',
    defaultVisible: true,
    noFilter: true,
    getValue: () => null,
    headerClassName: 'w-20',
  },
];

// =============================================================================
// Column visibility persistence
// =============================================================================

const VISIBILITY_STORAGE_KEY = 'atek-streams-column-visibility';

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of COLUMNS) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return getDefaultVisibility();
}

function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

// =============================================================================
// SortableHeader
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: StreamsFilters['sortKey'];
  currentSortKey: StreamsFilters['sortKey'];
  sortDirection: 1 | -1;
  onSort: (key: StreamsFilters['sortKey']) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1
          ? <ArrowUp className="ml-1 h-3 w-3" />
          : <ArrowDown className="ml-1 h-3 w-3" />
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// SearchableSelectFilter
// =============================================================================

function SearchableSelectFilter({
  colKey,
  options,
  value,
  onChange,
}: {
  colKey: string;
  options: FilterOption[];
  value: string;
  onChange: (key: string, value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const selected: string[] = useMemo(() => {
    try { return value ? JSON.parse(value) : []; }
    catch { return []; }
  }, [value]);

  const filtered = useMemo(() => {
    const base = search
      ? options.filter((o) => (o.label ?? o.value).toLowerCase().includes(search.toLowerCase()))
      : options;
    if (selected.length === 0) return base;
    const sel = new Set(selected);
    return [...base].sort((a, b) => (sel.has(a.value) ? 0 : 1) - (sel.has(b.value) ? 0 : 1));
  }, [options, search, selected]);

  const toggle = (opt: string) => {
    const next = selected.includes(opt)
      ? selected.filter((o) => o !== opt)
      : [...selected, opt];
    onChange(colKey, next.length ? JSON.stringify(next) : '');
  };

  const selectedLabel = useMemo(() => {
    if (selected.length === 0) return null;
    if (selected.length === 1) {
      const match = options.find((o) => o.value === selected[0]);
      return match?.label ?? '1 selected';
    }
    return `${selected.length} selected`;
  }, [selected, options]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-full text-xs justify-between font-normal pr-6"
          >
            <span className="truncate">{selectedLabel || '\u2014'}</span>
            <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {selected.length > 0 && (
          <button
            type="button"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 z-10"
            onClick={() => { onChange(colKey, ''); setOpen(false); }}
          >
            <X className="h-3 w-3 opacity-50 hover:opacity-100" />
          </button>
        )}
      </div>
      <PopoverContent className="w-56 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search..."
            value={search}
            onValueChange={setSearch}
            className="h-8 text-xs"
          />
          <CommandList>
            <CommandEmpty className="py-2 text-center text-xs text-muted-foreground">
              No results
            </CommandEmpty>
            <CommandGroup className="max-h-48 overflow-y-auto">
              {filtered.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.value}
                  onSelect={() => toggle(opt.value)}
                  className="text-xs"
                >
                  <div
                    className={cn(
                      'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                      selected.includes(opt.value)
                        ? 'bg-primary text-primary-foreground'
                        : 'opacity-50',
                    )}
                  >
                    {selected.includes(opt.value) && <Check className="h-3 w-3" />}
                  </div>
                  {opt.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function formatDateStr(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'yyyy-MM-dd');
  } catch {
    return dateStr;
  }
}

function formatDateTimeStr(dateStr: string | null): string {
  if (!dateStr) return '—';
  try {
    return format(new Date(dateStr), 'yyyy-MM-dd HH:mm');
  } catch {
    return dateStr;
  }
}

function isCalibrationOverdue(calibrationDue: string | null): boolean {
  if (!calibrationDue) return false;
  try {
    return new Date(calibrationDue) < new Date();
  } catch {
    return false;
  }
}

// =============================================================================
// StreamsTable
// =============================================================================

interface StreamsTableProps {
  streams: Stream[];
  pageItemCount: number;
  total: number;
  sortKey: StreamsFilters['sortKey'];
  sortDirection: 1 | -1;
  onSort: (key: StreamsFilters['sortKey']) => void;
  onEdit: (stream: Stream) => void;
  onDelete: (stream: Stream) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  columnFilters: Record<string, string>;
  onColumnFilterChange: (key: string, value: string) => void;
  onClearColumnFilters?: () => void;
  isLoading: boolean;
  onRefresh: () => void;
  isRefreshing: boolean;
  searchableSelectOptions?: Record<string, FilterOption[]>;
}

export function StreamsTable({
  streams,
  pageItemCount,
  total,
  sortKey,
  sortDirection,
  onSort,
  onEdit,
  onDelete,
  page,
  pageSize,
  onPageChange,
  columnFilters,
  onColumnFilterChange,
  onClearColumnFilters,
  isLoading,
  onRefresh,
  isRefreshing,
  searchableSelectOptions,
}: StreamsTableProps) {
  const { t } = useTranslation('streams');
  const totalPages = Math.ceil(total / pageSize) || 1;

  // ---------------------------------------------------------------------------
  // Column visibility
  // ---------------------------------------------------------------------------
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(loadColumnVisibility);

  const toggleColumn = (key: string) => {
    const updated = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(updated);
    saveColumnVisibility(updated);
  };

  const isVisible = (key: string) => columnVisibility[key] !== false;

  const hasActiveColumnFilters = Object.values(columnFilters).some(Boolean);

  const visibleColumns = useMemo(
    () => COLUMNS.filter((col) => isVisible(col.key)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnVisibility],
  );

  // ---------------------------------------------------------------------------
  // Export handlers
  // ---------------------------------------------------------------------------
  const handleCopy = () => {
    const exportCols = visibleColumns.filter((c) => !c.noFilter);
    const header = exportCols.map((c) => t(c.labelKey)).join('\t');
    const rows = streams.map((s) =>
      exportCols.map((c) => String(c.getValue(s) ?? '')).join('\t'),
    );
    navigator.clipboard.writeText([header, ...rows].join('\n'));
    toast.success(t('table.copied'));
  };

  const handleCSV = () => {
    const exportCols = visibleColumns.filter((c) => !c.noFilter);
    const header = exportCols.map((c) => escapeCSVValue(t(c.labelKey))).join(',');
    const rows = streams.map((s) =>
      exportCols.map((c) => escapeCSVValue(c.getValue(s))).join(','),
    );
    const csvContent = [header, ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `streams_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${streams.length} rows`);
  };

  // ---------------------------------------------------------------------------
  // Column filter rendering
  // ---------------------------------------------------------------------------
  const renderColumnFilter = (col: StreamColumnDef) => {
    if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

    // Searchable-select filter
    if (col.filterType === 'searchable-select') {
      const options = searchableSelectOptions?.[col.key] || [];
      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <SearchableSelectFilter
            colKey={col.key}
            options={options}
            value={columnFilters[col.key] || ''}
            onChange={onColumnFilterChange}
          />
        </TableHead>
      );
    }

    // Date range filter
    if (col.filterType === 'date-range') {
      const raw = columnFilters[col.key] || '';
      const [from = '', to = ''] = raw.split('>>');
      return (
        <TableHead key={`filter-${col.key}`} className="p-1">
          <div className="flex flex-col gap-0.5">
            <Input
              type="date"
              className="h-6 text-xs px-1"
              value={from}
              onChange={(e) => onColumnFilterChange(col.key, `${e.target.value}>>${to}`)}
              title={t('filters.from')}
            />
            <Input
              type="date"
              className="h-6 text-xs px-1"
              value={to}
              onChange={(e) => onColumnFilterChange(col.key, `${from}>>${e.target.value}`)}
              title={t('filters.to')}
            />
          </div>
        </TableHead>
      );
    }

    // Text filter (default)
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <Input
          className="h-7 text-xs"
          value={columnFilters[col.key] || ''}
          onChange={(e) => onColumnFilterChange(col.key, e.target.value)}
          placeholder="..."
        />
      </TableHead>
    );
  };

  // ---------------------------------------------------------------------------
  // Cell rendering
  // ---------------------------------------------------------------------------
  const renderCell = (col: StreamColumnDef, stream: Stream): React.ReactNode => {
    switch (col.key) {
      case 'calibrationDue': {
        const due = stream.calibrationDue;
        if (!due) return '—';
        const overdue = isCalibrationOverdue(due);
        return (
          <Badge variant={overdue ? 'destructive' : 'secondary'}>
            {formatDateStr(due)}
          </Badge>
        );
      }
      case 'updatedAt':
        return formatDateTimeStr(stream.updatedAt);
      case 'createdAt':
        return formatDateTimeStr(stream.createdAt);
      case 'actions':
        return (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-primary"
              onClick={(e) => { e.stopPropagation(); onEdit(stream); }}
              title={t('table.actions')}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={(e) => { e.stopPropagation(); onDelete(stream); }}
              title={t('table.actions')}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        );
      default:
        // Auto-format date columns
        if (col.filterType === 'date-range') {
          const raw = col.getValue(stream);
          return raw ? formatDateStr(String(raw)) : '—';
        }
        return String(col.getValue(stream) ?? '') || '—';
    }
  };

  // ---------------------------------------------------------------------------
  // Showing text
  // ---------------------------------------------------------------------------
  const showingText = streams.length < pageItemCount
    ? t('table.showingFiltered', { filtered: streams.length, pageCount: pageItemCount })
    : t('table.showing', { count: pageItemCount, total });

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      {/* Toolbar: showing text + export + refresh + column toggle */}
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <span className="text-sm text-muted-foreground">{showingText}</span>
        <div className="flex items-center gap-2">
          {hasActiveColumnFilters && onClearColumnFilters && (
            <Button variant="outline" size="sm" onClick={onClearColumnFilters}>
              <FilterX className="h-4 w-4 mr-1" /> Clear filters
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleCopy}>
            <Copy className="h-4 w-4 mr-1" />
            {t('table.copy')}
          </Button>
          <Button variant="outline" size="sm" onClick={handleCSV}>
            <Download className="h-4 w-4 mr-1" />
            {t('table.csv')}
          </Button>
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={isRefreshing}>
            <RefreshCw className={cn('h-4 w-4 mr-1', isRefreshing && 'animate-spin')} />
            {t('table.refresh')}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="h-4 w-4 mr-1" />
                {t('table.columns')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{t('table.toggleColumns')}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {COLUMNS.filter((c) => !c.noFilter).map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.key}
                  checked={isVisible(col.key)}
                  onCheckedChange={() => toggleColumn(col.key)}
                >
                  {t(col.labelKey)}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            {/* ROW 1: Filter inputs (always visible, only visible columns) */}
            <TableRow className="bg-muted/30 hover:bg-muted/30">
              {visibleColumns.map((col) => renderColumnFilter(col))}
            </TableRow>

            {/* ROW 2: Column labels + sort controls */}
            <TableRow>
              {visibleColumns.map((col) => (
                <TableHead
                  key={col.key}
                  className={cn(col.headerClassName)}
                >
                  {col.sortKey ? (
                    <SortableHeader
                      label={t(col.labelKey)}
                      sortKey={col.sortKey}
                      currentSortKey={sortKey}
                      sortDirection={sortDirection}
                      onSort={onSort}
                    />
                  ) : (
                    col.labelKey ? t(col.labelKey) : ''
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {/* Loading state */}
            {isLoading && Array.from({ length: 10 }).map((_, i) => (
              <TableRow key={i}>
                {visibleColumns.map((col) => (
                  <TableCell key={col.key}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))}

            {/* Empty state */}
            {!isLoading && streams.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={visibleColumns.length}
                  className="py-12 text-center text-muted-foreground"
                >
                  {t('table.noResults')}
                </TableCell>
              </TableRow>
            )}

            {/* Data rows */}
            {!isLoading && streams.map((stream) => (
              <TableRow key={stream.id} className="hover:bg-muted/50">
                {visibleColumns.map((col) => (
                  <TableCell
                    key={col.key}
                    className={cn('text-sm', col.cellClassName)}
                  >
                    {renderCell(col, stream)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t px-4 py-3 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t('table.page', { current: page + 1, total: totalPages })}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page - 1)}
              disabled={page === 0}
            >
              {t('table.previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages - 1}
            >
              {t('table.next')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
